import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  FileClock, Search, RefreshCw, X, Download, Shield,
  Copy, Check, ChevronRight, Laptop, Terminal,
  AlertTriangle, Info, ArrowRight, User
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAssetStore, type AuditLog } from '../../../store/assetStore';
import { cn } from '../../../lib/utils';
import { Pagination } from '../../../components/Pagination';

/** Readable label + badge tone for assets.asset_history.action values. */
const ACTIONS: Record<string, { label: string; code: string }> = {
  created: { label: 'Registered', code: 'asset.created' },
  updated: { label: 'Edited', code: 'asset.updated' },
  status_changed: { label: 'Status changed', code: 'asset.status_changed' },
  assigned: { label: 'Assigned', code: 'asset.action.CHECK_OUT' },
  returned: { label: 'Returned', code: 'asset.action.CHECK_IN' },
  retired: { label: 'Retired', code: 'asset.retired' },
  deleted: { label: 'Deleted', code: 'asset.deleted' },
  disposed: { label: 'Disposed', code: 'asset.disposed' },
  maintenance_scheduled: { label: 'Service scheduled', code: 'asset.maintenance.scheduled' },
  maintenance_in_progress: { label: 'Service started', code: 'asset.maintenance.start' },
  maintenance_completed: { label: 'Service completed', code: 'asset.maintenance.complete' },
  maintenance_cancelled: { label: 'Service cancelled', code: 'asset.maintenance.cancel' },
  po_received: { label: 'PO received', code: 'asset.po_received' },
  license_created: { label: 'Licence added', code: 'license.created' },
  license_updated: { label: 'Licence edited', code: 'license.updated' },
  license_deleted: { label: 'Licence removed', code: 'license.deleted' },
  license_assigned: { label: 'Seat assigned', code: 'license.seat.assign' },
  license_unassigned: { label: 'Seat released', code: 'license.seat.release' },
};

function getActionInfo(a: string) {
  const norm = (a || '').toLowerCase();
  if (ACTIONS[norm]) return ACTIONS[norm];
  return {
    label: norm.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
    code: norm.startsWith('asset.') ? norm : `asset.${norm}`,
  };
}

function getCategoryOfAction(action: string): 'check_in_out' | 'maintenance' | 'status' | 'registration' | 'licence' | 'general' {
  const a = (action || '').toLowerCase();
  if (a.includes('assign') || a.includes('return') || a.includes('check_in') || a.includes('check_out')) {
    return 'check_in_out';
  }
  if (a.includes('maintenance') || a.includes('service') || a.includes('repair')) {
    return 'maintenance';
  }
  if (a.includes('status') || a === 'retired' || a === 'disposed' || a === 'lost' || a === 'stolen') {
    return 'status';
  }
  if (a === 'created' || a.includes('po') || a === 'registered' || a === 'imported') {
    return 'registration';
  }
  if (a.includes('license') || a.includes('seat')) {
    return 'licence';
  }
  return 'general';
}

function getLevelOfAction(action: string, toStatus?: string | null): 'info' | 'warn' | 'error' | 'security' {
  const a = (action || '').toLowerCase();
  const s = (toStatus || '').toLowerCase();
  if (a.includes('delete') || a === 'stolen' || a === 'security_breach' || a === 'compromised') {
    return 'security';
  }
  if (a === 'retired' || a === 'disposed' || a.includes('cancel') || a.includes('error') || s === 'retired' || s === 'disposed') {
    return 'warn';
  }
  return 'info';
}

export function AssetHistoryView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { auditLogs, fetchHistory } = useAssetStore();

  const [loaded, setLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [isLiveStreaming, setIsLiveStreaming] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // URL-synced Filters
  const activeCategory = searchParams.get('category') || 'all';
  const activeLevel = searchParams.get('level') || 'all';
  const searchQuery = searchParams.get('q') || '';
  const dateFilter = searchParams.get('datePreset') || 'all';

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const updateFilter = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!value || value === 'all') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    }, { replace: true });
    setPage(1);
  }, [setSearchParams]);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setIsRefreshing(true);
    try {
      await fetchHistory({ limit: 500 });
    } catch {
      // handled
    } finally {
      setLoaded(true);
      setIsRefreshing(false);
    }
  }, [fetchHistory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Live streaming polling every 8s
  useEffect(() => {
    if (!isLiveStreaming) return;
    const interval = setInterval(() => {
      loadData(true);
    }, 8000);
    return () => clearInterval(interval);
  }, [isLiveStreaming, loadData]);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`Copied ${field} to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return dateStr;
    }
  };

  const formatRelativeTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '—';
      const diffMs = Date.now() - d.getTime();
      if (diffMs < 0) return 'Just now';
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHour / 24);

      if (diffSec < 45) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      if (diffHour < 24) return `${diffHour}h ago`;
      if (diffDay < 7) return `${diffDay}d ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '—';
    }
  };

  const getSeverityBadge = (sev?: string) => {
    const s = (sev || 'info').toLowerCase();
    if (s === 'security') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-red-100 text-red-800 border border-red-300 dark:bg-red-950/60 dark:text-red-200 dark:border-red-800">
          <Shield className="size-3 text-red-600" /> Security
        </span>
      );
    }
    if (s === 'error') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300">
          <AlertTriangle className="size-3 text-rose-600" /> Error
        </span>
      );
    }
    if (s === 'warn' || s === 'warning') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
          <AlertTriangle className="size-3 text-amber-600" /> Warning
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
        <Info className="size-3 text-emerald-600" /> Info
      </span>
    );
  };

  const getCategoryBadge = (category?: string) => {
    const c = (category || 'general').toLowerCase();
    const styleMap: Record<string, string> = {
      check_in_out: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300',
      maintenance: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300',
      status: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300',
      registration: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300',
      licence: 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300',
      general: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300',
    };
    const labels: Record<string, string> = {
      check_in_out: 'Check In/Out',
      maintenance: 'Maintenance',
      status: 'Status Changes',
      registration: 'Registrations',
      licence: 'Licences',
      general: 'General',
    };
    return (
      <span className={cn('px-2 py-0.5 rounded text-[11px] font-medium border uppercase tracking-wider', styleMap[c] || styleMap.general)}>
        {labels[c] || c}
      </span>
    );
  };

  const categoriesList = [
    { id: 'all', label: 'All Categories' },
    { id: 'check_in_out', label: 'Check In / Out' },
    { id: 'maintenance', label: 'Maintenance' },
    { id: 'status', label: 'Status Changes' },
    { id: 'registration', label: 'Registrations' },
    { id: 'licence', label: 'Licences' },
  ];

  const levelsList = [
    { id: 'all', label: 'All Levels' },
    { id: 'info', label: 'Info' },
    { id: 'warn', label: 'Warnings' },
    { id: 'error', label: 'Errors' },
    { id: 'security', label: 'Security' },
  ];

  // Dynamic KPI Metrics computation
  const summary = useMemo(() => {
    const totalEvents = auditLogs.length;
    const categories: Record<string, number> = {
      check_in_out: 0,
      maintenance: 0,
      status: 0,
      registration: 0,
      licence: 0,
    };
    let securityAlerts = 0;
    let warnings = 0;
    let errors = 0;
    let last24hEvents = 0;
    const now = Date.now();

    for (const log of auditLogs) {
      const cat = getCategoryOfAction(log.action);
      if (categories[cat] !== undefined) {
        categories[cat]++;
      }
      const lvl = getLevelOfAction(log.action, log.toStatus);
      if (lvl === 'security') securityAlerts++;
      else if (lvl === 'warn') warnings++;
      else if (lvl === 'error') errors++;

      if (log.timestamp) {
        const t = new Date(log.timestamp).getTime();
        if (!isNaN(t) && now - t <= 24 * 3600 * 1000) {
          last24hEvents++;
        }
      }
    }

    return {
      totalEvents,
      categories,
      securityAlerts,
      warnings,
      errors,
      last24hEvents,
    };
  }, [auditLogs]);

  // Filtering
  const filteredRows = useMemo(() => {
    let list = [...auditLogs];

    if (activeCategory !== 'all') {
      list = list.filter((l) => getCategoryOfAction(l.action) === activeCategory);
    }

    if (activeLevel !== 'all') {
      list = list.filter((l) => getLevelOfAction(l.action, l.toStatus) === activeLevel);
    }

    if (dateFilter === 'today') {
      const today = new Date().toDateString();
      list = list.filter((l) => l.timestamp && new Date(l.timestamp).toDateString() === today);
    } else if (dateFilter === '7d') {
      const cutoff = Date.now() - 7 * 86400 * 1000;
      list = list.filter((l) => l.timestamp && new Date(l.timestamp).getTime() >= cutoff);
    } else if (dateFilter === '30d') {
      const cutoff = Date.now() - 30 * 86400 * 1000;
      list = list.filter((l) => l.timestamp && new Date(l.timestamp).getTime() >= cutoff);
    }

    if (searchQuery.trim()) {
      const needle = searchQuery.trim().toLowerCase();
      list = list.filter((l) => {
        const actionInfo = getActionInfo(l.action);
        return (
          (l.assetCode && l.assetCode.toLowerCase().includes(needle)) ||
          (l.assetName && l.assetName.toLowerCase().includes(needle)) ||
          (l.details && l.details.toLowerCase().includes(needle)) ||
          (l.performedBy && l.performedBy.toLowerCase().includes(needle)) ||
          (l.fromStatus && l.fromStatus.toLowerCase().includes(needle)) ||
          (l.toStatus && l.toStatus.toLowerCase().includes(needle)) ||
          actionInfo.code.toLowerCase().includes(needle) ||
          actionInfo.label.toLowerCase().includes(needle)
        );
      });
    }

    return list;
  }, [auditLogs, activeCategory, activeLevel, dateFilter, searchQuery]);

  // Pagination slice
  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const validPage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (validPage - 1) * pageSize;
  const paginatedLogs = useMemo(() => {
    return filteredRows.slice(startIndex, startIndex + pageSize);
  }, [filteredRows, startIndex, pageSize]);

  // Client-side instant exports
  const handleExport = (format: 'csv' | 'json') => {
    try {
      if (format === 'csv') {
        const headers = ['ID', 'Timestamp', 'Asset Code', 'Asset Name', 'Action', 'Category', 'Level', 'From Status', 'To Status', 'Performed By', 'Details'];
        const rows = filteredRows.map((log) => [
          log.id,
          log.timestamp,
          `"${(log.assetCode || '').replace(/"/g, '""')}"`,
          `"${(log.assetName || '').replace(/"/g, '""')}"`,
          log.action,
          getCategoryOfAction(log.action),
          getLevelOfAction(log.action, log.toStatus),
          log.fromStatus || '',
          log.toStatus || '',
          `"${(log.performedBy || '').replace(/"/g, '""')}"`,
          `"${(log.details || '').replace(/"/g, '""')}"`,
        ]);
        const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
        const pad = (n: number) => String(n).padStart(2, '0');
        const now = new Date();
        const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `asset-audit-logs_${stamp}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Audit logs exported as CSV');
      } else {
        const pad = (n: number) => String(n).padStart(2, '0');
        const now = new Date();
        const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        const blob = new Blob([JSON.stringify(filteredRows, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `asset-audit-logs_${stamp}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Audit logs exported as JSON');
      }
    } catch {
      toast.error('Failed to export audit logs');
    }
  };

  // Related events for the inspector drawer
  const relatedLogs = useMemo(() => {
    if (!selectedLog) return [];
    return auditLogs.filter(
      (l) => l.id !== selectedLog.id && (
        (selectedLog.assetCode && l.assetCode === selectedLog.assetCode) ||
        (selectedLog.performedBy && l.performedBy === selectedLog.performedBy)
      )
    ).slice(0, 6);
  }, [selectedLog, auditLogs]);

  return (
    <div className="p-4 pb-12 space-y-4 bg-background min-h-full text-foreground text-xs font-sans animate-in fade-in duration-150">
      {/* 1. Header & Live Stream Card */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 bg-card border border-border p-4 rounded-lg shadow-2xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="size-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <FileClock className="size-5" />
            </div>
            <div>
              <h1 className="font-display text-page-title tracking-tight text-foreground">
                Audit Log & Compliance Trail
              </h1>
              <p className="text-xs text-muted-foreground">
                Immutable, append-only security logs · Asset transitions · Maintenance services · AI agent handoffs
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Live Stream Toggle */}
          <button
            onClick={() => setIsLiveStreaming(!isLiveStreaming)}
            className={cn(
              "text-xs font-semibold px-3 py-1.5 rounded-lg border flex items-center gap-2 transition-colors cursor-pointer",
              isLiveStreaming
                ? "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
                : "bg-secondary text-muted-foreground border-border hover:text-foreground"
            )}
            title="Real-time live streaming activity"
          >
            <span className={cn("size-2 rounded-full", isLiveStreaming ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground")} />
            {isLiveStreaming ? 'Live Stream: Active' : 'Live Stream: Paused'}
          </button>

          {/* Refresh button */}
          <button
            onClick={() => loadData()}
            disabled={isRefreshing}
            className="p-2 rounded-lg border border-border bg-card hover:bg-secondary text-foreground transition-colors disabled:opacity-50 cursor-pointer shadow-2xs"
            title="Refresh logs now"
          >
            <RefreshCw className={cn("size-3.5", isRefreshing && "animate-spin text-primary")} />
          </button>

          {/* Export Buttons */}
          <button
            onClick={() => handleExport('csv')}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border bg-card hover:bg-secondary text-foreground flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
          >
            <Download className="size-3.5 text-muted-foreground" />
            <span>CSV</span>
          </button>
          <button
            onClick={() => handleExport('json')}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border bg-card hover:bg-secondary text-foreground flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
          >
            <Download className="size-3.5 text-muted-foreground" />
            <span>JSON</span>
          </button>
        </div>
      </div>

      {/* 2. Top Metric Cards (Clickable to Filter) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div 
          onClick={() => { updateFilter('category', 'all'); updateFilter('level', 'all'); }}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-2xs cursor-pointer transition-all",
            activeCategory === 'all' && activeLevel === 'all' ? "border-primary/50 ring-1 ring-primary/30" : "border-border hover:border-border/80"
          )}
        >
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Events</span>
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-xl font-bold tracking-tight text-foreground">{summary.totalEvents}</span>
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">Persistent</span>
          </div>
        </div>

        <div 
          onClick={() => updateFilter('category', 'check_in_out')}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-2xs cursor-pointer transition-all",
            activeCategory === 'check_in_out' ? "border-primary/50 ring-1 ring-primary/30" : "border-border hover:border-border/80"
          )}
        >
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ticket Actions</span>
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-xl font-bold tracking-tight text-foreground">{summary.categories['check_in_out'] || 0}</span>
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40">Check In/Out</span>
          </div>
        </div>

        <div 
          onClick={() => updateFilter('level', 'security')}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-2xs cursor-pointer transition-all",
            activeLevel === 'security' ? "border-primary/50 ring-1 ring-primary/30" : "border-border hover:border-border/80"
          )}
        >
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Security Alerts</span>
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-xl font-bold tracking-tight text-foreground">{summary.securityAlerts}</span>
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40">Sec Audit</span>
          </div>
        </div>

        <div 
          onClick={() => updateFilter('level', 'warn')}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-2xs cursor-pointer transition-all",
            activeLevel === 'warn' ? "border-primary/50 ring-1 ring-primary/30" : "border-border hover:border-border/80"
          )}
        >
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Warnings & Errors</span>
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-xl font-bold tracking-tight text-foreground">{summary.warnings + summary.errors}</span>
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40">Needs Review</span>
          </div>
        </div>

        <div 
          onClick={() => updateFilter('datePreset', 'today')}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-2xs cursor-pointer transition-all",
            dateFilter === 'today' ? "border-primary/50 ring-1 ring-primary/30" : "border-border hover:border-border/80"
          )}
        >
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last 24 Hours</span>
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-xl font-bold tracking-tight text-foreground">{summary.last24hEvents}</span>
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40">Recent</span>
          </div>
        </div>
      </div>

      {/* 3. Filter Controls Card */}
      <div className="bg-card border border-border p-3.5 rounded-lg shadow-2xs space-y-3">
        {/* Category Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none border-b border-border/60">
          {categoriesList.map((cat) => (
            <button
              key={cat.id}
              onClick={() => updateFilter('category', cat.id)}
              className={cn(
                "px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5",
                activeCategory === cat.id
                  ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              )}
            >
              <span>{cat.label}</span>
              <span className={cn(
                "px-1.5 py-0.2 rounded-full text-[10px]",
                activeCategory === cat.id
                  ? "bg-primary-foreground/20 text-primary-foreground"
                  : "bg-secondary text-muted-foreground border border-border"
              )}>
                {cat.id === 'all' ? summary.totalEvents : (summary.categories[cat.id] ?? 0)}
              </span>
            </button>
          ))}
        </div>

        {/* Level Filters + Search + Date Controls */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap flex-1">
            {/* Level Tabs */}
            <div className="flex items-center rounded-lg border border-border p-0.5 bg-secondary/40">
              {levelsList.map((lvl) => (
                <button
                  key={lvl.id}
                  onClick={() => updateFilter('level', lvl.id)}
                  className={cn(
                    "px-2.5 py-1 text-xs rounded transition-colors cursor-pointer",
                    activeLevel === lvl.id
                      ? "bg-card text-foreground font-semibold shadow-2xs"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {lvl.label}
                </button>
              ))}
            </div>

            {/* Free Text Search */}
            <div className="relative min-w-[220px] flex-1 max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => updateFilter('q', e.target.value)}
                placeholder="Search action, asset code, user, details..."
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {searchQuery && (
                <button
                  onClick={() => updateFilter('q', '')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                >
                  <X className="size-3" />
                </button>
              )}
            </div>
          </div>

          {/* Date Range Presets */}
          <div className="flex items-center rounded-lg border border-border p-0.5 bg-secondary/40">
            {[
              { id: 'all', label: 'All Time' },
              { id: 'today', label: 'Today' },
              { id: '7d', label: '7 Days' },
              { id: '30d', label: '30 Days' },
            ].map((df) => (
              <button
                key={df.id}
                onClick={() => updateFilter('datePreset', df.id)}
                className={cn(
                  "px-2.5 py-1 text-xs rounded transition-colors cursor-pointer",
                  dateFilter === df.id
                    ? "bg-card text-foreground font-semibold shadow-2xs"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {df.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 4. Table */}
      <div className="rounded-lg border border-border bg-card shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-border bg-secondary/50 text-muted-foreground font-semibold uppercase tracking-wider">
                <th className="py-2.5 px-3.5 w-[140px]">Timestamp (24h)</th>
                <th className="py-2.5 px-3.5 w-[115px]">Category</th>
                <th className="py-2.5 px-3.5 w-[190px]">Action</th>
                <th className="py-2.5 px-3.5 w-[100px]">Level</th>
                <th className="py-2.5 px-3.5 w-[140px]">Target</th>
                <th className="py-2.5 px-3.5">Summary & Details</th>
                <th className="py-2.5 px-3.5 w-[150px]">Performed By</th>
                <th className="py-2.5 px-3.5 w-[60px] text-right">Inspect</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {!loaded ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="size-5 animate-spin text-primary" />
                      <p className="text-xs font-medium">Loading database audit logs...</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedLogs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <FileClock className="size-8 text-muted-foreground/40" />
                      <p className="text-xs font-semibold text-foreground">No audit logs found</p>
                      <p className="text-xs text-muted-foreground max-w-sm">
                        {searchQuery || activeCategory !== 'all' || activeLevel !== 'all' || dateFilter !== 'all'
                          ? 'No events matched your current filters. Adjust or clear filters to see more results.'
                          : 'Live audit records will appear here as actions occur in the platform.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedLogs.map((log) => {
                  const isSelected = selectedLog?.id === log.id;
                  const cat = getCategoryOfAction(log.action);
                  const lvl = getLevelOfAction(log.action, log.toStatus);
                  const actionInfo = getActionInfo(log.action);

                  return (
                    <tr
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className={cn(
                        "hover:bg-secondary/40 transition-colors cursor-pointer group",
                        isSelected && "bg-secondary/70 ring-1 ring-inset ring-primary/30"
                      )}
                    >
                      {/* Timestamp */}
                      <td className="py-2.5 px-3.5 text-muted-foreground whitespace-nowrap">
                        <span className="font-mono text-xs text-foreground font-medium block">
                          {formatRelativeTime(log.timestamp)}
                        </span>
                        <span className="text-[11px] text-muted-foreground block font-mono">
                          {formatDate(log.timestamp)}
                        </span>
                      </td>

                      {/* Category */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        {getCategoryBadge(cat)}
                      </td>

                      {/* Action */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        <span className="font-semibold text-xs text-foreground block font-mono">
                          {actionInfo.code}
                        </span>
                        <span className="text-[10px] text-muted-foreground block truncate max-w-[160px]">
                          {actionInfo.label}
                        </span>
                      </td>

                      {/* Level */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        {getSeverityBadge(lvl)}
                      </td>

                      {/* Target Asset */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        {log.assetCode ? (
                          <Link
                            to={`/assets/${encodeURIComponent(log.assetCode)}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-primary hover:underline"
                            title={`Open asset ${log.assetCode}`}
                          >
                            <Laptop className="size-3 text-primary/80 shrink-0" />
                            <span>{log.assetCode}</span>
                          </Link>
                        ) : (
                          <span className="text-muted-foreground font-mono">—</span>
                        )}
                        {log.assetName && (
                          <span className="text-[11px] text-muted-foreground block truncate max-w-[140px]">
                            {log.assetName}
                          </span>
                        )}
                      </td>

                      {/* Summary & Details */}
                      <td className="py-2.5 px-3.5 text-foreground">
                        <span className="block font-medium">
                          {log.details || actionInfo.label}
                        </span>
                        {log.fromStatus && log.toStatus && log.fromStatus !== log.toStatus && (
                          <div className="inline-flex items-center gap-1.5 mt-0.5 text-[11px] text-muted-foreground font-mono">
                            <span className="px-1.5 py-0.2 rounded bg-secondary text-muted-foreground border border-border">
                              {log.fromStatus}
                            </span>
                            <ArrowRight className="size-2.5 text-muted-foreground" />
                            <span className="px-1.5 py-0.2 rounded bg-primary/10 text-primary font-medium border border-primary/20">
                              {log.toStatus}
                            </span>
                          </div>
                        )}
                      </td>

                      {/* Actor / Performed By */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap text-muted-foreground font-medium">
                        <div className="flex items-center gap-1.5">
                          <div className="size-5 rounded-full bg-secondary border border-border flex items-center justify-center text-muted-foreground shrink-0 text-[10px] font-semibold">
                            {log.performedBy ? log.performedBy.charAt(0).toUpperCase() : 'S'}
                          </div>
                          <span className="text-xs text-foreground truncate max-w-[120px]">
                            {log.performedBy || 'System'}
                          </span>
                        </div>
                      </td>

                      {/* Inspect */}
                      <td className="py-2.5 px-3.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end">
                          <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 5. Pagination */}
        <div className="border-t border-border px-4 py-2.5 bg-card">
          <Pagination
            page={validPage}
            pageCount={totalPages}
            onPageChange={setPage}
            total={totalItems}
            pageSize={pageSize}
            onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
            itemLabel="Events"
          />
        </div>
      </div>

      {/* 6. Right Slide-Over Inspector Drawer */}
      {selectedLog && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in"
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="w-full max-w-lg bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center justify-between bg-card">
              <div className="flex items-center gap-2.5">
                <div className="size-9 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0">
                  <Terminal className="size-4" />
                </div>
                <div>
                  <h2 className="font-display text-sm font-semibold text-foreground uppercase tracking-wider">
                    Asset Event Inspection
                  </h2>
                  <span className="font-mono text-[11px] text-muted-foreground block">
                    ID: {selectedLog.id}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => copyToClipboard(JSON.stringify(selectedLog, null, 2), 'Event JSON')}
                  className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground cursor-pointer"
                  title="Copy JSON"
                >
                  {copiedField === 'Event JSON' ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedLog(null)}
                  className="size-8 rounded-lg hover:bg-secondary text-muted-foreground flex items-center justify-center transition-colors cursor-pointer"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Event Overview Card */}
              <div className="rounded-lg border border-border bg-muted/20 p-3.5 space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Shield className="size-3.5 text-primary" />
                  <span>Event Overview</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Action</span>
                    <span className="font-semibold text-foreground font-mono">
                      {getActionInfo(selectedLog.action).code}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Performed By</span>
                    <span className="font-semibold text-foreground">{selectedLog.performedBy || 'System'}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Timestamp</span>
                    <span className="font-mono text-foreground">{formatDate(selectedLog.timestamp)}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Severity Level</span>
                    <div className="mt-0.5">
                      {getSeverityBadge(getLevelOfAction(selectedLog.action, selectedLog.toStatus))}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className="text-[11px] text-muted-foreground block">Status Transition</span>
                    <span className="font-semibold text-foreground">
                      {selectedLog.fromStatus && selectedLog.toStatus ? `${selectedLog.fromStatus} → ${selectedLog.toStatus}` : 'Direct Action / Event'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Asset Reference Card */}
              <div className="rounded-lg border border-border bg-muted/20 p-3.5 space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Laptop className="size-3.5 text-primary" />
                  <span>Target Asset</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Asset Code</span>
                    {selectedLog.assetCode ? (
                      <button
                        onClick={() => {
                          setSelectedLog(null);
                          navigate(`/assets/${encodeURIComponent(selectedLog.assetCode)}`);
                        }}
                        className="font-mono font-semibold text-primary hover:underline text-left cursor-pointer"
                      >
                        {selectedLog.assetCode}
                      </button>
                    ) : (
                      <span className="font-mono text-muted-foreground">—</span>
                    )}
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Asset Name</span>
                    <span className="font-semibold text-foreground">{selectedLog.assetName || '—'}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-[11px] text-muted-foreground block">Details & Notes</span>
                    <span className="text-foreground">{selectedLog.details || 'No additional note provided.'}</span>
                  </div>
                </div>
              </div>

              {/* Related Activity */}
              {relatedLogs.length > 0 && (
                <div className="rounded-lg border border-border bg-muted/20 p-3.5 space-y-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <FileClock className="size-3.5 text-primary" />
                    <span>Related Asset Activity ({relatedLogs.length})</span>
                  </div>
                  <div className="space-y-2">
                    {relatedLogs.map((rel) => (
                      <div
                        key={rel.id}
                        onClick={() => setSelectedLog(rel)}
                        className="p-2 rounded bg-card border border-border hover:border-primary/40 cursor-pointer flex items-center justify-between transition-colors"
                      >
                        <div>
                          <span className="font-mono text-[11px] font-semibold text-foreground block">
                            {getActionInfo(rel.action).code}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {formatRelativeTime(rel.timestamp)} by {rel.performedBy || 'System'}
                          </span>
                        </div>
                        <ChevronRight className="size-3 text-muted-foreground" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw JSON Payload */}
              <div className="rounded-lg border border-border bg-card p-3.5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Audit Payload Record
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(JSON.stringify(selectedLog, null, 2), 'Payload')}
                    className="text-[11px] text-primary hover:underline cursor-pointer flex items-center gap-1"
                  >
                    <Copy className="size-3" /> Copy
                  </button>
                </div>
                <pre className="p-2.5 rounded bg-secondary/50 font-mono text-[11px] text-foreground overflow-x-auto max-h-48 scrollbar-thin">
                  {JSON.stringify(selectedLog, null, 2)}
                </pre>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border flex items-center justify-end gap-2 bg-card">
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="px-3.5 py-1.5 rounded-lg border border-border hover:bg-secondary text-xs font-medium text-foreground cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
